import { useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Loader2 } from 'lucide-react';
import type { ChatStatus } from 'ai';
import type { AgentUIMessage, ChatSource } from '@/lib/chat/types';
import { extractMessageText, extractSources, extractTimeline } from '@/lib/chat/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ToolTimeline } from './ToolTimeline';
import { SourcesList } from './SourcesList';
import { cn } from '@/lib/utils';

interface ChatThreadProps {
  messages: AgentUIMessage[];
  status: ChatStatus;
  totalPages: number;
  onOpenInAppSource: (source: ChatSource) => void;
  className?: string;
}

export function ChatThread({ messages, status, totalPages, onOpenInAppSource, className }: ChatThreadProps) {
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null);
  const isStreaming = status === 'streaming' || status === 'submitted';

  useEffect(() => {
    if (isStreaming) return;
    bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [isStreaming, messages.length]);

  const showThinking = useMemo(() => {
    if (status !== 'streaming' && status !== 'submitted') return false;
    const lastMessage = messages[messages.length - 1];
    return !lastMessage || lastMessage.role !== 'assistant';
  }, [messages, status]);

  return (
    <ScrollArea className={cn('min-h-0 flex-1', className)}>
      <div className="space-y-4 p-4">
        {messages.length === 0 && (
          <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-600">
            Ask anything about your saved pages. I only use your library and always cite sources.
          </div>
        )}

        {messages.map(message => {
          const text = extractMessageText(message);
          const timeline = extractTimeline(message);
          const sources = extractSources(message);
          const isAssistant = message.role === 'assistant';
          const isLatestMessage = message === messages[messages.length - 1];
          const isActiveAssistantMessage =
            isAssistant && isLatestMessage && (status === 'streaming' || status === 'submitted');

          return (
            <div key={message.id} className={cn('flex', isAssistant ? 'justify-start' : 'justify-end')}>
              {isAssistant ? (
                <div className="w-full max-w-[92%] rounded-lg border border-neutral-200 bg-white p-3 text-sm">
                  <ToolTimeline
                    entries={timeline}
                    durationMs={message.metadata?.durationMs}
                    isStreaming={isActiveAssistantMessage}
                    hasText={Boolean(text)}
                  />

                  {text ? (
                    <div className="prose mt-3 max-w-none text-sm text-neutral-800 [&>*+*]:mt-4 [&_h1]:mt-6 [&_h2]:mt-5 [&_h3]:mt-4 [&_ul]:my-3 [&_ol]:my-3 [&_li+li]:mt-1.5">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                    </div>
                  ) : status === 'streaming' || status === 'submitted' ? (
                    <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Thinking over {totalPages} pages...
                    </div>
                  ) : (
                    <p className="mt-3 text-neutral-500">No text response.</p>
                  )}

                  <SourcesList sources={sources} onOpenInApp={onOpenInAppSource} />
                </div>
              ) : (
                <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2.5 text-sm text-white">
                  <p className="whitespace-pre-wrap">{text}</p>
                </div>
              )}
            </div>
          );
        })}

        {showThinking && (
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Searching {totalPages} pages and synthesizing...
            </span>
          </div>
        )}

        <div ref={bottomAnchorRef} />
      </div>
    </ScrollArea>
  );
}
